import type { ITaskLogEntry, LocationStatus, TaskStatus } from '@interfaces/IScheduler';

// ---------------------------------------------------------------------------
// taskLog — OPTIMISTIC mirror of the server-generated execution log.
//
// Phase 2: the execution log has exactly one author — the server
// (`ActivityService`). Clients never persist it. But so the drawer timeline
// updates INSTANTLY on an execution write (and converges on the next load), the
// execution reducers append the SAME structured entry the server will derive,
// with the SAME coalescing rule (a slider session becomes one entry, not
// twenty). This module owns that construction + coalescing so the reducers and
// their tests share one implementation and can't drift from the backend
// (`backend/src/core/activities/activity.service.ts`).
// ---------------------------------------------------------------------------

/** Coalescing window (ms): a same-kind, same-user, same-target entry younger
 *  than this is UPDATED in place rather than appended. Matches the backend. */
export const COALESCE_WINDOW_MS = 10 * 60 * 1000;

/** Log kinds whose successive entries coalesce (a session collapses to one). */
export type CoalesceableKind = Extract<
  ITaskLogEntry,
  { kind: 'status' | 'progress' | 'location' }
>['kind'];

/** An entry whose kind is one of the coalesceable kinds. */
type CoalesceableEntry = Extract<ITaskLogEntry, { kind: CoalesceableKind }>;

const COALESCEABLE_KINDS: ReadonlySet<CoalesceableKind> = new Set<CoalesceableKind>([
  'status',
  'progress',
  'location',
]);

/** Type guard narrowing an entry to the coalesceable subset. */
function isCoalesceableEntry(entry: ITaskLogEntry): entry is CoalesceableEntry {
  return (COALESCEABLE_KINDS as ReadonlySet<ITaskLogEntry['kind']>).has(entry.kind);
}

/** Exhaustiveness helper: TS fails to compile if a switch drops a case. */
function assertNever(value: never): never {
  throw new Error(`taskLog: unreachable kind ${JSON.stringify(value)}`);
}

/** Identity of the acting user + the moment/id of the write. */
export interface ITaskLogMeta {
  id: string;
  at: string;
  by: string;
}

export function statusEntry(meta: ITaskLogMeta, from: TaskStatus, to: TaskStatus): ITaskLogEntry {
  return { id: meta.id, at: meta.at, by: meta.by, kind: 'status', data: { from, to } };
}

export function progressEntry(meta: ITaskLogMeta, from: number, to: number): ITaskLogEntry {
  return { id: meta.id, at: meta.at, by: meta.by, kind: 'progress', data: { from, to } };
}

export function locationEntry(
  meta: ITaskLogMeta,
  locationId: string,
  from: LocationStatus | undefined,
  to: LocationStatus,
): ITaskLogEntry {
  return {
    id: meta.id,
    at: meta.at,
    by: meta.by,
    kind: 'location',
    data: { locationId, ...(from ? { from } : {}), to },
  };
}

export function checkEntry(meta: ITaskLogMeta, itemId: string, completed: boolean): ITaskLogEntry {
  return { id: meta.id, at: meta.at, by: meta.by, kind: 'check', data: { itemId, completed } };
}

export function commentEntry(meta: ITaskLogMeta, commentId: string): ITaskLogEntry {
  return { id: meta.id, at: meta.at, by: meta.by, kind: 'comment', data: { commentId } };
}

/** The coalescing target of an entry (same target ⇒ same log line). */
function targetOf(entry: ITaskLogEntry): string {
  if (entry.kind === 'location') return `location:${entry.data.locationId}`;
  if (entry.kind === 'check') return `check:${entry.data.itemId}`;
  return entry.kind; // status/progress are task-level
}

/** Merge `next` into `last`: keep `last`'s identity + `from`, take `next`'s
 *  moving field (`to`) and timestamp. Same-kind by construction —
 *  `last` is narrowed per-branch (its actual kind is guaranteed to match
 *  `next.kind` by the caller, `appendTaskLogEntry`). */
function coalesceInto(last: ITaskLogEntry, next: CoalesceableEntry): ITaskLogEntry {
  const kind = next.kind;
  switch (kind) {
    case 'status': {
      const prev = last as Extract<ITaskLogEntry, { kind: 'status' }>;
      return { ...prev, at: next.at, data: { from: prev.data.from, to: next.data.to } };
    }
    case 'progress': {
      const prev = last as Extract<ITaskLogEntry, { kind: 'progress' }>;
      return { ...prev, at: next.at, data: { from: prev.data.from, to: next.data.to } };
    }
    case 'location': {
      const prev = last as Extract<ITaskLogEntry, { kind: 'location' }>;
      const to = next.data.to !== undefined ? next.data.to : prev.data.to;
      return {
        ...prev,
        at: next.at,
        data: {
          locationId: prev.data.locationId,
          ...(prev.data.from !== undefined ? { from: prev.data.from } : {}),
          ...(to !== undefined ? { to } : {}),
        },
      };
    }
    default:
      return assertNever(kind);
  }
}

/**
 * Return a NEW log with `next` applied: UPDATE the last entry in place when it
 * is the same coalesceable kind, by the same user, on the same target, and
 * younger than the window; otherwise APPEND. Pure (never mutates `log`), so
 * reducers assign the result and Immer diffs it.
 */
export function appendTaskLogEntry(
  log: readonly ITaskLogEntry[],
  next: ITaskLogEntry,
): ITaskLogEntry[] {
  const last = log[log.length - 1];
  if (
    last &&
    isCoalesceableEntry(next) &&
    last.kind === next.kind &&
    last.by === next.by &&
    targetOf(last) === targetOf(next) &&
    Date.parse(next.at) - Date.parse(last.at) < COALESCE_WINDOW_MS
  ) {
    return [...log.slice(0, -1), coalesceInto(last, next)];
  }
  return [...log, next];
}
